import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PROPERTIES, PROPERTY_TYPES, SORT_OPTIONS, LOCATIONS,
  loadFavorites, saveFavorites, formatPrice,
} from './propertyData';
import PropertyCard from './PropertyCard';
import PropertyModal from './PropertyModal';
import './RealEstate.css';

function RealEstate() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState(loadFavorites);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showFavOnly, setShowFavOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyType, setPropertyType] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 3000000]);
  const [bedsMin, setBedsMin] = useState(0);
  const [location, setLocation] = useState('All');
  const [showFilters, setShowFilters] = useState(true);

  const toggleFavorite = (id) => {
    const updated = favorites.includes(id)
      ? favorites.filter((f) => f !== id)
      : [...favorites, id];
    setFavorites(updated);
    saveFavorites(updated);
  };

  const filtered = useMemo(() => {
    let result = PROPERTIES.filter((p) => {
      if (showFavOnly && !favorites.includes(p.id)) return false;
      if (searchQuery && !p.title.toLowerCase().includes(searchQuery.toLowerCase()) && !p.location.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (propertyType !== 'All' && p.type !== propertyType) return false;
      if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
      if (p.beds < bedsMin) return false;
      if (location !== 'All' && p.location !== location) return false;
      return true;
    });

    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'newest': result.sort((a, b) => b.createdAt - a.createdAt); break;
      case 'sqft': result.sort((a, b) => b.sqft - a.sqft); break;
      default: result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)); break;
    }
    return result;
  }, [searchQuery, propertyType, sortBy, priceRange, bedsMin, location, showFavOnly, favorites]);

  const avgPrice = filtered.length > 0
    ? Math.round(filtered.reduce((s, p) => s + p.price, 0) / filtered.length)
    : 0;

  return (
    <div className="re-app">
      <div className="re-topbar">
        <div className="re-topbar-left">
          <button className="re-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="re-title">Properties</h1>
        </div>
        <div className="re-topbar-center">
          <div className="re-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="re-topbar-right">
          <button
            className={`re-fav-toggle ${showFavOnly ? 'active' : ''}`}
            onClick={() => setShowFavOnly(!showFavOnly)}
          >
            ♥ {favorites.length}
          </button>
          <div className="re-view-toggle">
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')} title="Grid">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')} title="List">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          </div>
          <button className="re-filter-btn" onClick={() => setShowFilters(!showFilters)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filters
          </button>
        </div>
      </div>

      <div className="re-body">
        {/* Filters Sidebar */}
        <div className={`re-filters ${showFilters ? 'open' : ''}`}>
          <div className="re-filter-section">
            <h3>Property Type</h3>
            <div className="re-type-btns">
              {PROPERTY_TYPES.map((type) => (
                <button
                  key={type}
                  className={`re-type-btn ${propertyType === type ? 'active' : ''}`}
                  onClick={() => setPropertyType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="re-filter-section">
            <h3>Price Range</h3>
            <div className="re-price-display">
              <span>{formatPrice(priceRange[0])}</span>
              <span>—</span>
              <span>{formatPrice(priceRange[1])}</span>
            </div>
            <input
              type="range" min="0" max="3000000" step="50000"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
              className="re-range"
            />
          </div>

          <div className="re-filter-section">
            <h3>Bedrooms</h3>
            <div className="re-bed-btns">
              {[0, 1, 2, 3, 4, 5].map((b) => (
                <button
                  key={b}
                  className={`re-bed-btn ${bedsMin === b ? 'active' : ''}`}
                  onClick={() => setBedsMin(b)}
                >
                  {b === 0 ? 'Any' : `${b}+`}
                </button>
              ))}
            </div>
          </div>

          <div className="re-filter-section">
            <h3>Location</h3>
            <select className="re-location-select" value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="All">All Locations</option>
              {LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div className="re-filter-section">
            <h3>Sort By</h3>
            <select className="re-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          <button className="re-clear-filters" onClick={() => {
            setPropertyType('All'); setPriceRange([0, 3000000]); setBedsMin(0);
            setLocation('All'); setSortBy('featured'); setSearchQuery(''); setShowFavOnly(false);
          }}>
            Clear All Filters
          </button>
        </div>

        {/* Main Content */}
        <div className="re-main">
          <div className="re-results-bar">
            <span className="re-results-count">{filtered.length} properties found</span>
            {filtered.length > 0 && (
              <span className="re-results-avg">Avg: {formatPrice(avgPrice)}</span>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className={`re-grid ${viewMode}`}>
              {filtered.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  isFavorite={favorites.includes(property.id)}
                  onToggleFavorite={() => toggleFavorite(property.id)}
                  onClick={() => setSelectedProperty(property)}
                  viewMode={viewMode}
                />
              ))}
            </div>
          ) : (
            <div className="re-empty">
              <span className="re-empty-icon">🏠</span>
              <h3>No properties found</h3>
              <p>Try adjusting your filters to see more results.</p>
            </div>
          )}
        </div>
      </div>

      {selectedProperty && (
        <PropertyModal
          property={selectedProperty}
          isFavorite={favorites.includes(selectedProperty.id)}
          onToggleFavorite={() => toggleFavorite(selectedProperty.id)}
          onClose={() => setSelectedProperty(null)}
        />
      )}
    </div>
  );
}

export default RealEstate;
